import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Backdrop,
  Box,
  Button,
  Container,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Modal,
  TextField,
  Typography,
} from "@mui/material";

import { postNotification } from "../../../components/utilities/Notifications";
import ModalLoadingFull from "../../../components/modals/ModalLoadingFull";

import * as Yup from "yup";
import { Formik } from "formik";
import { userDisplayName } from "./EventFunctions";
import ListResponsive from "../../../components/other/ListResponsive";
import { handlePagingAll } from "../../../components/api/APIUtilities";
import GenericPicker from "../../../components/utilities/GenericPicker";
import {
  BCPLinkSimple,
  BCPLockKeyhole,
  BCPLockKeyholeOpen,
  BCPSquarePlus,
  BCPUserMagnifyingGlass,
  BCPXmark,
} from "../../../components/icons/bcp/BCPIcons";
import {
  getData,
  postData,
} from "../../../components/utilities/UtilityFunctions";
import { getEnvironmentVariable } from "../../../global/environmentVariables";
const NO_SUBSCRIPTIONS = getEnvironmentVariable("NO_SUBSCRIPTIONS") === "true";

export default function EventUserRoles(props) {
  const { event, authContext } = props;

  const { t } = useTranslation("translation", {
    keyPrefix: "components.EventUserRoles",
  });

  const [readOnly, setReadOnly] = useState(
    event && !event?.userRole?.permissions?.eventDetails
  );

  const [processing, setProcessing] = useState(false);
  const [subLoading, setSubLoading] = useState(true);

  const [eventUsers, setEventUsers] = useState(null);
  const [eventUserDict, setEventUserDict] = useState({});
  const [eventUser, setEventUser] = useState(null);

  const [eventRoles, setEventRoles] = useState(null);
  const [eventRolesDict, setEventRolesDict] = useState(null);

  useEffect(() => {
    const check = async () => {
      if (
        !NO_SUBSCRIPTIONS &&
        !authContext?.authState?.identity?.subscriber &&
        !authContext?.authState?.identity?.isAdmin
      ) {
        setReadOnly(true);
        postNotification("warning", t("subscriberOnlyWarning"));
      }

      var apiEventRoles = await handlePagingAll(`/eventroles`, {
        limit: 100,
      });

      var eventRolesArray = [];
      var apiEventRolesDict = {};
      apiEventRoles.forEach((apiEventRole) => {
        eventRolesArray.push({
          value: apiEventRole.id,
          label: apiEventRole.name,
        });
        apiEventRolesDict[apiEventRole.id] = apiEventRole;
      });

      setEventRoles(eventRolesArray);
      setEventRolesDict(apiEventRolesDict);

      let tempEventUserDict = {};
      let apiEventUsers = [];
      const eventOwnerId = event?.ownerId ?? event?.owner?.id ?? undefined;

      if (event?.eventUsers?.length) {
        event.eventUsers.forEach((user) => {
          if (user?.id) tempEventUserDict[user.id] = { ...user };
        });
      }

      if (
        eventOwnerId &&
        typeof eventOwnerId === "string" &&
        !tempEventUserDict[eventOwnerId]
      ) {
        tempEventUserDict[eventOwnerId] = {
          id: eventOwnerId,
          firstName: event.owner?.firstName || event.ownerFirstName,
          lastName: event.owner?.lastName || event.ownerLastName,
          email: event.owner?.email || event.ownerEmail,
          role: {
            id: apiEventRolesDict.DefaultRole?.id,
            name: apiEventRolesDict.DefaultRole?.name,
            orderNum: apiEventRolesDict.DefaultRole?.orderNum || 0,
          },
        };
      }

      setEventUserDict(tempEventUserDict);
      apiEventUsers = Object.values(tempEventUserDict);

      apiEventUsers.sort((a, b) => {
        if (
          a.role?.orderNum !== undefined &&
          b.role?.orderNum !== undefined &&
          a.role?.orderNum !== b.role?.orderNum
        ) {
          return a.role?.orderNum - b.role?.orderNum;
        }
        if (
          a.role?.name !== undefined &&
          b.role?.name !== undefined &&
          a.role?.name !== b.role?.name
        ) {
          var roleA = a.role.name.toLowerCase();
          var roleB = b.role.name.toLowerCase();
          if (roleA > roleB) return 1;
          if (roleA < roleB) return -1;
        }
        var nameA = userDisplayName(a.firstName, a.lastName).toLowerCase();
        var nameB = userDisplayName(b.firstName, b.lastName).toLowerCase();
        if (nameA > nameB) return 1;
        if (nameA < nameB) return -1;
        return 0;
      });

      setEventUsers(apiEventUsers);

      setSubLoading(false);
    };
    check();
    // eslint-disable-next-line
  }, []);

  return (
    <Container sx={{ pt: 2, pb: 2 }} maxWidth="lg">
      <Grid container sx={{ gap: 2 }}>
        <Grid
          item
          xs={12}
          sx={{
            p: 2,
          }}
        >
          <Typography variant="h3">{event.name}</Typography>

          <Typography variant="h4">{t("eventUsers")}</Typography>
        </Grid>

        {(authContext?.authState?.identity?.isAdmin === true ||
          authContext?.authState?.identity?.adminGameSystems?.includes(
            event?.gameSystemId
          )) && (
          <Grid
            item
            xs={12}
            sx={{
              p: 2,
            }}
          >
            <Typography variant="h5">
              <Button
                color="primary"
                variant="outlined"
                fullWidth
                onClick={() => {
                  setReadOnly(!readOnly);
                }}
                endIcon={
                  readOnly ? (
                    <BCPLockKeyhole outlined />
                  ) : (
                    <BCPLockKeyholeOpen outlined />
                  )
                }
              >
                {readOnly ? t("locked") : t("unlocked")}
              </Button>
            </Typography>
          </Grid>
        )}

        <Grid
          item
          xs={12}
          sx={{
            p: 2,
          }}
        >
          <ListResponsive
            originalList={subLoading ? null : eventUsers}
            doSearch
            doPages
            initialRowsPerPage={25}
            actionButton={[
              {
                props: {
                  variant: "contained",
                  color: "primary",
                  onClick: () => {
                    setEventUser({});
                  },
                  startIcon: <BCPSquarePlus />,
                  disabled: readOnly,
                },
                label: t("addUser"),
              },
            ]}
            searchProps={{
              label: t("searchUsers"),
              performSearch: (originalList, searchText) => {
                if (!searchText || searchText === "") {
                  return originalList;
                } else {
                  var filteredList = originalList.filter(function (el) {
                    if (el?.firstName?.toLowerCase().includes(searchText))
                      return true;
                    if (el?.lastName?.toLowerCase().includes(searchText))
                      return true;
                    if (el?.email?.toLowerCase().includes(searchText))
                      return true;
                    if (el?.role?.name?.toLowerCase().includes(searchText))
                      return true;

                    return false;
                  });
                  return filteredList;
                }
              },
            }}
            rowProps={{
              mapFunction: (row, index) => (
                <Grid
                  key={index}
                  item
                  xs={12}
                  onClick={
                    readOnly
                      ? null
                      : (e) => {
                          setEventUser(row);
                        }
                  }
                  sx={{
                    p: 1,

                    "&:hover": {
                      cursor: "pointer",
                    },
                  }}
                >
                  <Typography variant="body1">{`${row.firstName} ${row.lastName}`}</Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      textTransform: "capitalize",
                    }}
                  >
                    {row.role?.name}
                    {row.id === event.ownerId && (
                      <span
                        sx={{
                          fontWeight: "600px",
                        }}
                      >
                        {t("owner")}
                      </span>
                    )}
                  </Typography>
                  <Divider sx={{ mb: 1, mt: 1 }} />
                </Grid>
              ),
            }}
          />
        </Grid>
      </Grid>
      <EventUserModal
        event={event}
        eventUser={eventUser}
        setEventUser={setEventUser}
        eventUsers={eventUsers}
        setEventUsers={setEventUsers}
        eventUserDict={eventUserDict}
        setEventUserDict={setEventUserDict}
        eventRoles={eventRoles}
        eventRolesDict={eventRolesDict}
        setProcessing={setProcessing}
      />

      {Boolean(processing) && <ModalLoadingFull />}
    </Container>
  );
}

function EventUserModal(props) {
  const {
    event,
    eventUser,
    setEventUser,
    eventUsers,
    eventUserDict,
    setEventUserDict,
    setEventUsers,

    eventRoles,
    eventRolesDict,

    setProcessing,
  } = props;

  const { t } = useTranslation("translation", {
    keyPrefix: "components.EventUserRoles",
  });

  const isOwner = event?.ownerId && eventUser?.id === event?.ownerId;
  const [user, setUser] = useState(null);

  const fetchUser = async (email) => {
    var apiUser;
    if (email !== "") {
      try {
        apiUser = await getData(`/users/${email}`, {
          queryStringParameters: {},
        });
      } catch (err) {}
    }
    return apiUser;
  };

  const searchUser = async (email, setFieldValue) => {
    setProcessing(true);
    var apiUser = await fetchUser(email);
    setProcessing(false);

    if (
      apiUser?.id &&
      eventUsers?.filter(function (el) {
        return el?.id === apiUser.id;
      }).length > 0
    ) {
      postNotification("error", t("userAlreadyAssigned"));

      setFieldValue("id", null);
      setFieldValue("firstName", "");
      setFieldValue("lastName", "");
      return;
    }
    setUser(apiUser);

    setFieldValue("id", apiUser?.id || null);
    setFieldValue("firstName", apiUser?.firstName || "");
    setFieldValue("lastName", apiUser?.lastName || "");

    if (!apiUser?.id) {
      postNotification("error", t("userNotFound"));
    }
  };

  useEffect(() => {
    const go = async () => {
      var apiUser = null;
      if (eventUser?.email) {
        setProcessing(true);
        apiUser = await fetchUser(eventUser.email);
        setProcessing(false);
      }
      setUser(apiUser);
    };
    go();
    // eslint-disable-next-line
  }, [eventUser]);

  return (
    <Modal
      disableAutoFocus
      open={Boolean(eventUser)}
      onClose={() => {
        setEventUser(null);
      }}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
      sx={{ zIndex: 9999 }}
    >
      {eventUser ? (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: ["95vw", "85vw", 600],
            maxHeight: "95vh",
            overflowY: "scroll",
            bgcolor: "background.paper",
            border: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: 1,
            boxShadow: 24,
            p: 4,
          }}
        >
          <IconButton
            aria-label="cancel"
            color="error"
            sx={{ position: "absolute", top: "4px", right: "4px" }}
            onClick={() => {
              setEventUser(null);
            }}
          >
            <BCPXmark />
          </IconButton>
          <Grid
            container
            spacing={2}
            sx={{
              pb: 1,
              mb: 2,
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
            }}
          >
            <Grid item xs>
              <Typography variant="h4" component="h2">
                {isOwner
                  ? t("eventOwner")
                  : eventUser.id
                    ? t("updateUser")
                    : t("addUserModal")}
              </Typography>
            </Grid>
          </Grid>
          <Formik
            initialValues={{
              id: eventUser?.id || null,
              email: eventUser?.email || "",
              firstName: eventUser?.firstName || "",
              lastName: eventUser?.lastName || "",

              roleId: eventUser?.role?.id || "",
            }}
            enableReinitialize
            validationSchema={Yup.object().shape({
              email: Yup.string()
                .max(128)
                .email(t("validEmailRequired"))
                .required(t("required")),
              roleId: Yup.string().required(t("required")),
            })}
            onSubmit={async (values) => {
              if (eventRolesDict[values.roleId]?.isJudge && !user?.isJudge) {
                postNotification(
                  "error",
                  `${
                    user
                      ? userDisplayName(user.firstName, user.lastName)
                      : "User"
                  } may not be set as a ${eventRolesDict[values.roleId].name}`
                );
                return;
              }
              if (
                eventRolesDict[values.roleId]?.isOrganizer &&
                !user?.isOrganizer
              ) {
                postNotification(
                  "error",
                  `${
                    user
                      ? userDisplayName(user.firstName, user.lastName)
                      : "User"
                  } may not be set as a ${eventRolesDict[values.roleId].name}`
                );
                return;
              }

              setProcessing(true);

              try {
                var newEventUsers = {
                  ...eventUserDict,
                  [values.id]: {
                    id: values.id,
                    firstName: values.firstName,
                    lastName: values.lastName,
                    email: values.email,
                    role: {
                      id: values.roleId,
                      name: eventRolesDict[values.roleId]?.name,
                      orderNum: eventRolesDict[values.roleId]?.orderNum || 0,
                    },
                  },
                };

                // legacy apps
                var newTournamentOrganizers = [
                  {
                    __type: "Pointer",
                    className: "_User",
                    objectId: event.ownerId,
                  },
                ];
                Object.values(newEventUsers).forEach((newEventUser) => {
                  if (event.ownerId !== newEventUser.id) {
                    newTournamentOrganizers.push({
                      __type: "Pointer",
                      className: "_User",
                      objectId: newEventUser.id,
                    });
                  }
                });

                await postData(`/events/${event?.id}`, {
                  body: {
                    set: {
                      eventUsers: newEventUsers,
                      tournamentOrganizers: newTournamentOrganizers,
                    },
                  },
                });

                setEventUserDict(newEventUsers);

                var newEventUsersArray = Object.values(newEventUsers);
                setEventUsers(newEventUsersArray);

                setEventUser(null);
                setProcessing(false);
                return;
              } catch (err) {
                postNotification("error", err);
                setProcessing(false);
              }
            }}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              touched,
              values,
              setFieldValue,
            }) => (
              <form onSubmit={handleSubmit}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <TextField
                      label={t("email")}
                      name="email"
                      value={values.email}
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      disabled={Boolean(eventUser.id)}
                      required
                      InputProps={{
                        endAdornment: eventUser.id ? (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label={t("copyEmail")}
                              edge="end"
                              color="primary"
                              onClick={() => {
                                navigator.clipboard.writeText(values.email);
                              }}
                            >
                              <BCPLinkSimple />
                            </IconButton>
                          </InputAdornment>
                        ) : (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label={t("search")}
                              edge="end"
                              color="primary"
                              onClick={async () => {
                                await searchUser(values.email, setFieldValue);
                              }}
                            >
                              <BCPUserMagnifyingGlass />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      onBlur={async (e) => {
                        handleBlur(e);
                        await searchUser(e.target.value, setFieldValue);
                      }}
                      onChange={(e) => {
                        const trimmedString = e.target.value
                          .trim()
                          .toLowerCase();
                        setFieldValue("email", trimmedString);
                      }}
                      error={Boolean(touched.email && errors.email)}
                      helperText={touched.email && errors.email}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label={t("firstName")}
                      value={values.firstName}
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label={t("lastName")}
                      value={values.lastName}
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      disabled
                    />
                  </Grid>
                  {eventUser?.id && event.ownerId !== eventUser.id && (
                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        variant="outlined"
                        color="error"
                        onClick={async () => {
                          if (!eventUser?.id) return;
                          if (eventUser?.id === event.ownerId) return;

                          setProcessing(true);
                          try {
                            var newEventUsers = {
                              ...eventUserDict,
                            };
                            delete newEventUsers[eventUser?.id];
                            var newTournamentOrganizers = [
                              {
                                __type: "Pointer",
                                className: "_User",
                                objectId: event.ownerId,
                              },
                            ];
                            Object.values(newEventUsers).forEach(
                              (newEventUser) => {
                                if (
                                  event.ownerId !== newEventUser.id &&
                                  newEventUser.roles?.tournamentOrganizer
                                ) {
                                  newTournamentOrganizers.push({
                                    __type: "Pointer",
                                    className: "_User",
                                    objectId: newEventUser.id,
                                  });
                                }
                              }
                            );

                            await postData(`/events/${event?.id}`, {
                              body: {
                                set: {
                                  eventUsers: newEventUsers,
                                  tournamentOrganizers: newTournamentOrganizers,
                                },
                              },
                            });

                            setEventUserDict(newEventUsers);

                            var newEventUsersArray =
                              Object.values(newEventUsers);
                            setEventUsers(newEventUsersArray);

                            setEventUser(null);
                            setProcessing(false);
                            return;
                          } catch (err) {
                            postNotification("error", err);
                            setProcessing(false);
                          }
                        }}
                      >
                        {t("remove")}
                      </Button>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <GenericPicker
                      label={t("role")}
                      required
                      inclAny={false}
                      name="roleId"
                      list={eventRoles}
                      value={values.roleId}
                      handleChange={handleChange}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        setEventUser(null);
                      }}
                    >
                      {t("cancel")}
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="success"
                      type="submit"
                      disabled={isOwner || !values.id}
                    >
                      {t("save")}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </Box>
      ) : (
        <></>
      )}
    </Modal>
  );
}
